if(!customElements.get('sticky-header')){
    customElements.define('sticky-header', class StickyHeader extends HTMLElement {
        constructor() {
            super();

            this.stickyType = this.getAttribute('data-sticky-type') || 'none';
            if(this.stickyType === 'none') return;

            this.currentScrollTop = 0;
            this.onScrollHandler = this.onScroll.bind(this);
            this.preventHide = false;
            this.preventReveal = false;
            this.isHidden = false;
            this.cachedHeaderHeight = 100;
            this._abScrolledPast = false;
            this.hideHeaderOnScrollUp = () => this.preventReveal = true;
        }

        connectedCallback() {
            // Scope to header-v2 only — other sections (header.liquid,
            // jpw-hybrid-header) have .section-header but NOT .section-header-v2.
            // This prevents rogue instances (customizer previews, chat widget)
            // from fighting over the AB and MainContent.
            this.header = this.closest('.section-header-v2');
            if (!this.header) return;

            this.headerIsAlwaysSticky = this.stickyType === 'always' || this.stickyType === 'reduce-logo-size';

            this.predictiveSearch = this.querySelector('predictive-search');
            this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);

            this.headerEl = this.header.querySelector('header');
            this.mainContent = document.getElementById('MainContent');

            // Sticky announcement bar setting (mobile only)
            this.stickyAbMobile = this.hasAttribute('data-sticky-announcement-mobile');
            this.mobileQuery = window.matchMedia('(max-width: 749px)');


            // Find the announcement bar and its Shopify section wrapper
            this.announcementBar = document.querySelector('.announcement-bar');
            this.abSection = this.announcementBar ? this.announcementBar.closest('.shopify-section') : null;
            this.abHeight = 0;

            if (this.announcementBar) {
                this.abHeight = this.announcementBar.offsetHeight || 0;
            }

            // Make AB section fixed — eliminates per-frame top interpolation
            if (this.abSection && this.abHeight > 0) {
                this.abSection.style.position = 'fixed';
                this.abSection.style.top = '0';
                this.abSection.style.left = '0';
                this.abSection.style.right = '0';
                this.abSection.style.zIndex = '2';
                this.abSection.style.willChange = 'transform';
            }

            // Header fixed below the AB
            this.header.style.position = 'fixed';
            this.header.style.top = this.abHeight + 'px';
            this.header.style.left = '0';
            this.header.style.right = '0';
            this.header.style.transform = 'translateY(0)';
            this.header.style.willChange = 'transform';
            this.header.classList.add('shopify-section-header-sticky');
            this.header.style.overflowAnchor = 'none';

            // If page loads already scrolled past AB, snap immediately
            if (this.abHeight > 0 && this.getScrollTop() >= this.abHeight) {
                this._abScrolledPast = true;
                if (this._keepAbSticky()) {
                    // AB stays visible, header stays below it
                } else {
                    this.header.style.top = '0px';
                    if (this.abSection) this.abSection.style.transform = 'translateY(-100%)';
                }
            }

            this.compensateMainContent();
            this.setHeaderHeight();

            window.matchMedia('(min-width: 750px)').addEventListener('change', () => {
                this.updateAbHeight();
                this.compensateMainContent();
                this.setHeaderHeight();
            });

            // Re-measure after layout settles
            requestAnimationFrame(() => {
                this.updateAbHeight();
                this.compensateMainContent();
                this.setHeaderHeight();
                requestAnimationFrame(() => {
                    // Transition for hide/reveal (transform) + AB snap (top)
                    this.header.style.transition = 'transform 300ms ease-out, top 150ms ease-out';
                    if (this.abSection) {
                        this.abSection.style.transition = 'transform 150ms ease-out';
                    }
                });
            });

            setTimeout(() => {
                this.updateAbHeight();
                this.compensateMainContent();
                this.setHeaderHeight();
            }, 500);

            // Single scroll listener
            window.addEventListener('scroll', this.onScrollHandler, { passive: true });

            // Keep in sync with header height changes
            const observeEl = this.headerEl || this.header;
            if (observeEl && typeof ResizeObserver !== 'undefined') {
                this.resizeObserver = new ResizeObserver(() => {
                    this.compensateMainContent();
                    this.setHeaderHeight();
                });
                this.resizeObserver.observe(observeEl);
            }

            if (this.announcementBar && typeof ResizeObserver !== 'undefined') {
                this.abResizeObserver = new ResizeObserver(() => {
                    this.updateAbHeight();
                    this.compensateMainContent();
                });
                this.abResizeObserver.observe(this.announcementBar);
            }
        }

        disconnectedCallback() {
            window.removeEventListener('scroll', this.onScrollHandler);
            if (this.resizeObserver) this.resizeObserver.disconnect();
            if (this.abResizeObserver) this.abResizeObserver.disconnect();
        }

        // Returns true if the AB should stay visible (sticky mobile setting + on mobile)
        _keepAbSticky() {
            return this.stickyAbMobile && this.mobileQuery.matches;
        }

        updateAbHeight() {
            if (!this.announcementBar) return;
            this.abHeight = this.announcementBar.offsetHeight || 0;
        }

        getHeaderHeight() {
            if (!this.headerEl) return 0;
            return this.headerEl.offsetHeight || 0;
        }

        compensateMainContent() {
            if (!this.mainContent) return;
            const hh = this.getHeaderHeight();
            // Both AB section and header are fixed (out of flow),
            // so main needs padding for the full stack
            if (hh > 0) {
                this.mainContent.style.paddingTop = (this.abHeight + hh) + 'px';
            }
        }

        getScrollTop() {
            const se = document.scrollingElement || document.documentElement;
            return se.scrollTop;
        }

        setHeaderHeight() {
            const hh = this.getHeaderHeight();
            if (hh > 0) {
                this.cachedHeaderHeight = hh;
                document.documentElement.style.setProperty('--header-height', hh + 'px');
            }
        }

        onScroll() {
            const scrollTop = this.getScrollTop();

            if (document.body.hasAttribute('scroll-y-off') || this.predictiveSearch && this.predictiveSearch.isOpen) return;

            if (this.preventReveal && (!this.predictiveSearch || !this.predictiveSearch.isOpen)) {
                this.preventReveal = false;
            }

            // Snap header position at AB threshold
            if (this.abHeight > 0) {
                const keepSticky = this._keepAbSticky();

                if (keepSticky) {
                    // Mobile sticky: AB always visible, header always below it.
                    // Actively enforce every frame to override any external changes.
                    if (this.abSection) this.abSection.style.transform = 'translateY(0)';
                    this.header.style.top = this.abHeight + 'px';
                    this._abScrolledPast = scrollTop >= this.abHeight;
                } else if (scrollTop >= this.abHeight && !this._abScrolledPast) {
                    this._abScrolledPast = true;
                    this.header.style.top = '0px';
                    if (this.abSection) this.abSection.style.transform = 'translateY(-100%)';
                } else if (scrollTop < this.abHeight && this._abScrolledPast) {
                    this._abScrolledPast = false;
                    this.header.style.top = this.abHeight + 'px';
                    if (this.abSection) this.abSection.style.transform = 'translateY(0)';
                }
            }

            if (this.stickyType === 'on-scroll-up') {
                if (scrollTop > 10) {
                    this.header.classList.add('scrolled-past-header');
                } else {
                    this.header.classList.remove('scrolled-past-header');
                }

                const headerHeight = this.cachedHeaderHeight;
                if (scrollTop <= headerHeight) {
                    this.reveal();
                    this.currentScrollTop = scrollTop;
                    return;
                }

                const delta = scrollTop - this.currentScrollTop;
                if (delta > 3 && !this.preventHide) {
                    this.hide();
                } else if (delta < -3 && !this.preventReveal) {
                    this.reveal();
                }

                this.currentScrollTop = scrollTop;
                return;
            }

            if (scrollTop > 10) {
                this.header.classList.add('scrolled-past-header');
            } else {
                this.header.classList.remove('scrolled-past-header');
            }

            this.currentScrollTop = scrollTop;
        }

        hide() {
            if (this.isHidden) return;
            this.isHidden = true;
            // When AB is sticky on mobile, header is at top: abHeight.
            // translateY(-100%) only shifts by own height, leaving the bottom
            // visible behind the AB. Extra offset clears it fully.
            if (this._keepAbSticky()) {
                this.header.style.transform = `translateY(calc(-100% - ${this.abHeight}px))`;
            } else {
                this.header.style.transform = 'translateY(-100%)';
            }
            this.header.classList.add('shopify-section-header-hidden');
            this.closeDropMenus();
        }

        reveal() {
            if (!this.isHidden) return;
            this.isHidden = false;
            this.header.style.transform = 'translateY(0)';
            this.header.classList.remove('shopify-section-header-hidden');
        }

        reset() {
            this.reveal();
        }

        closeDropMenus() {
            this.dropMenus = this.dropMenus || this.header.querySelectorAll('drop-menu');
            this.dropMenus.forEach(dropMenu => dropMenu.close());
        }
    });
}
